import React, { useEffect, useRef, useState } from 'react';
import '../styles/style.css';
import '../styles/note.css';
import Calculator from './Calculator.jsx';
import ShortcutHelp from './ShortcutHelp.jsx';
import Note from './Note.jsx';
import BoardEdges from './BoardEdges.jsx';
import BoardImage from './BoardImage.jsx';
import { AppHeader, BoardToolbar, EmptyBoard, BoardFooter, BoardToast } from './BoardChrome.jsx';
import useTheme from '../hooks/useTheme.js';
import useBoardPan from '../hooks/useBoardPan.js';
import useBoardZoom from '../hooks/useBoardZoom.js';
import useBoardCursor from '../hooks/useBoardCursor.js';
import useNoteBoard from '../hooks/useNoteBoard.js';
import useNoteClipboard from '../hooks/useNoteClipboard.js';
import { listBoards, createBoard, importBoards, deleteBoard, loadActiveBoard, saveActiveBoard } from '../util/storage.js';
import { exportBoard, parseBoards } from '../util/boardTransfer.mjs';
import { boardSize, noteHeight, noteWidth } from '../util/notes.mjs';

const App = function() {
  const [boards, setBoards] = useState([]);
  const [active, setActiveState] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const setActive = function(id) {
    saveActiveBoard(id);
    setActiveState(id);
  };

  useEffect(() => {
    let cancelled = false;

    listBoards().then(saved => {
      if (cancelled) return;
      setBoards(saved);
      setActive(loadActiveBoard(saved));
    }).catch(() => {
      if (cancelled) return;
      setError('boards could not be loaded');
      setActiveState('main');
    });

    return () => { cancelled = true; };
  }, []);

  const addBoard = async function() {
    if (creating) return;

    setCreating(true);

    try {
      const board = await createBoard(boards);

      setBoards(current => [...current, board]);
      setActive(board.id);
      setError('');
    } catch {
      setError('new board could not be saved. please try again.');
    } finally {
      setCreating(false);
    }
  };

  const importFile = async function(file) {
    if (!file || creating) return;

    setCreating(true);

    try {
      const imported = parseBoards(await file.text());
      const saved = await importBoards(boards, imported);

      setBoards(current => [...current, ...saved]);
      setActive(saved[0].id);
      setError('');
    } catch (error) {
      setError(error.message || 'boards could not be imported.');
    } finally {
      setCreating(false);
    }
  };

  const duplicateBoard = async function(name, notes) {
    if (creating) return;

    setCreating(true);

    try {
      const copies = parseBoards(exportBoard(`${name || 'my board'} copy`, notes));
      const saved = await importBoards(boards, copies);

      setBoards(current => [...current, ...saved]);
      setActive(saved[0].id);
      setError('');
    } catch {
      setError('board could not be duplicated. please try again.');
    } finally {
      setCreating(false);
    }
  };

  const removeBoard = async function() {
    if (creating || !boards.length) return;

    setCreating(true);

    try {
      const remaining = await deleteBoard(boards, active);

      setBoards(remaining);
      setActive(remaining[0].id);
      setError('');
    } catch {
      setError('board could not be deleted. please try again.');
    } finally {
      setCreating(false);
    }
  };

  const renameActiveBoard = function(name) {
    setBoards(current => current.map(board => (
      board.id === active ? {...board, name} : board
    )));
  };

  // Resolve the saved selection before mounting a board and starting autosave.
  if (!active) return null;

  return (
    <BoardApp
      key={active}
      boardId={active}
      boards={boards}
      onBoardChange={setActive}
      onNewBoard={addBoard}
      creating={creating}
      boardError={error}
      onImport={importFile}
      onDeleteBoard={removeBoard}
      onDuplicateBoard={duplicateBoard}
      onBoardRename={renameActiveBoard}
    />
  );
};

const BoardApp = function({
  boardId,
  boards,
  onBoardChange,
  onNewBoard,
  creating,
  boardError,
  onBoardRename,
  onImport,
  onDeleteBoard,
  onDuplicateBoard
}) {
  const boardRef = useRef(null);
  const boardPosition = useBoardCursor(boardRef);

  const {theme, toggleTheme} = useTheme();
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [calculatorAnchor, setCalculatorAnchor] = useState(null);
  const pointerRef = useRef(null);

  useEffect(() => {
    const trackPointer = event => {
      pointerRef.current = {x: event.clientX, y: event.clientY};
    };

    document.addEventListener('pointermove', trackPointer);
    return () => document.removeEventListener('pointermove', trackPointer);
  }, []);
  const [helpOpen, setHelpOpen] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(() => {
    try {
      return localStorage.getItem('stickii-snap-to-grid') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('stickii-snap-to-grid', String(snapToGrid));
    } catch {
      // Snapping still works when browser storage is unavailable.
    }
  }, [snapToGrid]);
  const {pan, reveal, setPan} = useBoardPan(boardRef);
  const {zoom, changeZoom} = useBoardZoom(boardRef, setPan);

  const {
    notes,
    boardName,
    setBoardName,
    ready,
    selected,
    setSelected,
    saveStatus,
    message,
    setMessage,
    deletedNote,
    updateNote,
    selectNote,
    addNote,
    appendContent,
    deleteNote,
    duplicateNote,
    undo,
    redo,
    undoDelete,
    dismissMessage
  } = useNoteBoard(boardRef, boardId);

  const {pasteClipboard} = useNoteClipboard({
    ready,
    appendContent,
    boardPosition,
    notes,
    selected,
    deleteNote,
    addNote,
    onError: setMessage
  });

  const size = boardSize(notes);

  const downloadBoard = function() {
    const blob = new Blob([exportBoard(boardName, notes)], {
      type: 'application/json'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${boardName.replace(/[^a-z0-9_-]/gi, '_') || 'board'}.stickii.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Allow the browser to start the download before releasing the URL.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const fitAll = function() {
    if (!notes.length) return;

    const left = Math.min(...notes.map(note => note.x));
    const top = Math.min(...notes.map(note => note.y));
    const width = Math.max(...notes.map(note => note.x + noteWidth(note))) - left;
    const height = Math.max(...notes.map(note => note.y + noteHeight(note))) - top;
    const board = boardRef.current;

    // Leave room around the notes and avoid enlarging them beyond their actual size.
    const next = Math.max(0.05, Math.min(1, (board.clientWidth - 64) / width, (board.clientHeight - 64) / height));

    changeZoom(next);

    reveal({
      x: left,
      y: top,
      width,
      height
    }, next);
  };

  useEffect(() => {
    const handleBoardShortcut = function(event) {
      if (!ready || event.defaultPrevented || event.repeat || event.isComposing || event.altKey || helpOpen) return;

      const editing = event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])');

      if (event.key === 'Escape') {
        if (editing) event.target.blur();
        else setSelected(null);

        boardRef.current.focus({
          preventScroll: true
        });

        return;
      }

      // Keep board shortcuts from interrupting typing in editors and controls.
      if (editing) return;

      const key = event.key.toLowerCase();
      const modifier = event.ctrlKey || event.metaKey;

      let action;

      if (modifier) {
        if (key === 'd' && selected) action = () => duplicateNote(selected);

        if (key === 'z') action = event.shiftKey ? redo : undo;
        if (key === 'y') action = redo;
      } else if ((key === 'backspace' || key === 'delete') && selected) {
        action = () => {
          deleteNote(selected);
          boardRef.current.focus({
            preventScroll: true
          });
        };
      } else if (key === 'c') {
        action = () => {
          if (!calculatorOpen) setCalculatorAnchor(pointerRef.current ? {...pointerRef.current} : null);
          setCalculatorOpen(value => !value);
        };
      } else if (key === 'n') {
        const position = boardPosition();

        if (position) action = () => addNote({}, position);
      } else if (key === 'f') {
        action = fitAll;
      } else if (key === '0') {
        action = () => changeZoom(1);
      } else if (key === '+' || key === '=') {
        action = () => changeZoom(zoom + 0.25);
      } else if (key === '-') {
        action = () => changeZoom(zoom - 0.25);
      } else if (key === 'g') {
        action = () => setSnapToGrid(value => !value);
      } else if (key === '?') {
        action = () => setHelpOpen(true);
      }

      if (action) {
        event.preventDefault();
        action();
      }
    };

    document.addEventListener('keydown', handleBoardShortcut);

    return () => document.removeEventListener('keydown', handleBoardShortcut);
  }, [calculatorOpen, ready, helpOpen, selected, deletedNote, notes, zoom, addNote, boardPosition, deleteNote, duplicateNote, undo, redo, changeZoom, reveal]);

  const handleBoardPointerDown = function(event) {
    if (event.target.closest('.note, button, input, summary')) return;

    setSelected(null);

    // Paste events follow focus: empty canvas clicks must leave the note editor.
    boardRef.current.focus({
      preventScroll: true
    });
  };

  const handleBoardDoubleClick = function(event) {
    if (!event.target.matches('.board, .boardCanvas')) return;

    const board = boardRef.current;
    const bounds = board.getBoundingClientRect();

    // Convert viewport coordinates to positions on the canvas.
    addNote({}, {
      x: (event.clientX - bounds.left - pan.x) / zoom,
      y: (event.clientY - bounds.top - pan.y) / zoom
    });
  };

  const renameBoard = function(name) {
    setBoardName(name);
    onBoardRename(name);
  };

  const revealItem = function(note) {
    reveal({...note, width: noteWidth(note), height: noteHeight(note)}, zoom);
    selectNote(note.id);
  };

  const renderItem = function(note) {
    const Tile = note.kind === 'image' ? BoardImage : Note;

    return (
      <Tile
        key={note.id}
        note={note}
        layer={note.layer || 0}
        selected={selected === note.id}
        onSelect={selectNote}
        onChange={updateNote}
        onDelete={deleteNote}
        onPaste={pasteClipboard}
      />
    );
  };

  return (
    <div id='app' className='app v'>
      <AppHeader
        ready={ready}
        onAdd={() => addNote()}
        onNewBoard={onNewBoard}
        creating={creating || !boards.length}
      />
      <BoardToolbar
        onDuplicateBoard={() => onDuplicateBoard(boardName, notes)}
        onDeleteBoard={onDeleteBoard}
        onExport={downloadBoard}
        onImport={onImport}
        importing={creating}
        boards={boards}
        boardId={boardId}
        onBoardChange={onBoardChange}
        name={boardName}
        onRename={renameBoard}
        ready={ready}
      />

      <main
        ref={boardRef}
        className='board'
        data-zoom={zoom}
        data-snap={snapToGrid}
        data-pan-x={pan.x}
        data-pan-y={pan.y}
        tabIndex={0}
        aria-label='Sticky note board. Double click empty space to add a note.'
        onPointerDown={handleBoardPointerDown}
        onDoubleClick={handleBoardDoubleClick}
      >
        <div
          className='boardCanvas'
          style={{
            width: size.width,
            height: size.height,
            zoom,
            translate: `${pan.x / zoom}px ${pan.y / zoom}px`
          }}
        >
          {!ready && <p className='boardEmpty'>Opening your board…</p>}
          {ready && notes.length === 0 && <EmptyBoard onAdd={() => addNote()} />}
          {notes.map(renderItem)}
        </div>
      </main>
      <BoardEdges
        boardRef={boardRef}
        notes={notes}
        zoom={zoom}
        pan={pan}
        onReveal={revealItem}
      />

      <Calculator anchor={calculatorAnchor} open={calculatorOpen} onClose={() => setCalculatorOpen(false)} />
      <BoardFooter
        calculatorOpen={calculatorOpen}
        onCalculatorToggle={() => {
          setCalculatorAnchor(null);
          setCalculatorOpen(value => !value);
        }}
        theme={theme}
        onThemeToggle={toggleTheme}
        saveStatus={saveStatus}
        zoom={zoom}
        onZoomChange={changeZoom}
        onFit={fitAll}
        hasItems={notes.length > 0}
        onHelp={() => setHelpOpen(true)}
        snapToGrid={snapToGrid}
        onSnapToggle={() => setSnapToGrid(current => !current)}
      />
      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <BoardToast
        message={boardError || message}
        canUndo={Boolean(deletedNote)}
        onUndo={undoDelete}
        onDismiss={dismissMessage}
      />
    </div>
  );
};

export default App;
